/* eslint-disable */
/* eslint-disable eslint-comments/no-unused-disable */
/* 
* The file is automatically generated by Serenity UI.
* 
* ⚠️ WARNING: Any manual changes may be overwritten during the next upgrade
* 
* Found an issue? Submit a task to the ClickUp board:
* https://app.clickup.com/1255586/v/l/6-901510012553-1?pr=90154030902
*/
import * as stylex from '@stylexjs/stylex';
import type { ReactNode } from 'react';
import { type ForwardedRef, type HTMLAttributes, createContext, forwardRef } from 'react';
import { type ContextValue, DEFAULT_SLOT, Provider, useContextProps, useRenderProps } from 'react-aria-components';
import { $ } from '../../tokens.stylex';
import { TextContext } from '../text/text.component';

interface PasswordIndicatorProps extends Omit<HTMLAttributes<HTMLDivElement>, 'style' | 'className' | 'children'> {
  /** Password strength level. (Default: 0) */
  value: keyof typeof strengthVariants;
  style?: stylex.StyleXStyles;
  children?: ((values: {
    value: keyof typeof strengthVariants;
    valueText: string;
    defaultChildren: ReactNode | undefined;
  }) => ReactNode) | ReactNode;
}

const PasswordIndicatorContext = createContext<ContextValue<PasswordIndicatorProps, HTMLDivElement>>({});

const PasswordIndicator = forwardRef((props: PasswordIndicatorProps, ref: ForwardedRef<HTMLDivElement>) => {
  [props, ref] = useContextProps(props, ref, PasswordIndicatorContext);

  const { style, value = 0, children, ...coreProps } = props;

  const renderProps = useRenderProps({
    ...coreProps,
    values: {
      value,
      valueText: VALUE_TEXT[value]
    },
    defaultChildren: (
      <div {...stylex.props(styles.barContainer)} role="presentation">
        {Array.from({ length: 4 }, (_, i) => (
          <div
            key={i}
            {...stylex.props(styles.bar, i < value && strengthVariants[value])}
            aria-hidden="true"
          />
        ))}
      </div>
    ),
    children: (renderProps) => (
      <Provider values={[
        [TextContext, {
          slots: {
            [DEFAULT_SLOT]: {},
            [PASSWORD_INDICATOR_SLOTS.DESCRIPTION]: {
              size: 12,
              weight: 'regular',
              color: 'tertiary',
              style: styles.label,
            }
          }
        }]
      ]}>
        {typeof children === 'function' ? children(renderProps) : children || renderProps.defaultChildren}
      </Provider>
    )
  });

  return (
    <div
      ref={ref}
      role="progressbar"
      aria-label="Password strength"
      aria-valuemin={0}
      aria-valuemax={4}
      aria-valuenow={value}
      aria-valuetext={VALUE_TEXT[value]}
      {...renderProps}
      {...coreProps}
      {...stylex.props(styles.base, style)}>
      {renderProps.children}
    </div>
  );
});

const VALUE_TEXT = {
  0: 'Very weak',
  1: 'Weak',
  2: 'Moderate',
  3: 'Strong',
  4: 'Very strong'
};

const styles = stylex.create({
  base: {
    alignItems: 'center',
    display: 'inline-flex',
    gap: 12,
    position: 'relative',
  },
  barContainer: {
    alignItems: 'center',
    display: 'inline-flex',
    flexGrow: 1,
    gap: 4,
    position: 'relative',
  },
  bar: {
    backgroundColor: $.opacity10,
    borderRadius: 999,
    flexBasis: 0,
    flexGrow: 1,
    flexShrink: 1,
    height: 2,
    transition: 'background-color 0.2s ease-in-out',
    width: 32,
  },
  label: {
    width: 65,
  }
});

const strengthVariants = stylex.create({
  0: {},
  1: { backgroundColor: $.accentChilli60 },
  2: { backgroundColor: $.accentPumpkin60 },
  3: { backgroundColor: $.accentSun60 },
  4: { backgroundColor: $.accentCucumber60 },
});

const PASSWORD_INDICATOR_SLOTS = {
  DESCRIPTION: 'description',
};

export { PASSWORD_INDICATOR_SLOTS, PasswordIndicator, PasswordIndicatorContext, type PasswordIndicatorProps };
